import { useLocation, useNavigate } from 'react-router-dom'
import { PageTitle, TabBody } from '../../components/PageTitle'
import { AlertListPage } from '../alerts/AlertListPage'
import { EventStreamPage } from '../events/EventStreamPage'
import { ExplorePage } from '../explore/ExplorePage'
import { withTab } from '../../lib/tabs'
import { activeTabFor, screenFor, tabsFor } from '../../lib/screens'
import { session } from '../../lib/api'

/**
 * Triage, and the raw detections underneath it.
 *
 * Alerts and Events were two destinations and are one. An alert was already a fold - occurrences
 * sharing a device, an assistant, a rule set and a fifteen-minute bucket become one case with a count
 * - and the raw feed is that fold opened up. Presenting them as separate screens asked an operator to
 * know which of two places held the thing they wanted, when the answer is that they are the same
 * thing at two levels of detail.
 *
 * Query is the third tab, and it is here rather than gone. The design folds Explore into "the query
 * bar that is always present", and the bar is the right entry point - but a query surface is more
 * than an input: Explore validates as you type and says where a query is wrong, saves one, and
 * exports a result. Deleting the screen before the bar can do those would be removing working
 * features and calling it a redesign. The bar will open this tab; the tab is where the answer lands.
 *
 * The tab lives in the URL rather than in state because `/events`, `/explore` and the raw feed all
 * redirect to it, this console is navigated by URL in the end-to-end suite, and a link somebody sends
 * a colleague should open what they were looking at.
 *
 * The three tabs, their labels and the `?feed=raw` spelling are declared in `lib/screens.ts`. That
 * spelling predates this strip and is what `/events` redirects to, so it is honored as a spelling of
 * `?tab=raw` rather than being a second thing to keep in step - and it used to be honored *here*, by
 * the one `URLSearchParams` in the console that answered "which tab" without the helper. That made
 * this the only screen where the redirect table and the tab strip were two separate facts. Declaring
 * the alias as data makes them one, and `activeTabFor` reads it in the order this file read it: the
 * alias first, then `tab`.
 */
const SCREEN = screenFor('/cases')

export function CasesPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const roles = session.identity()?.roles ?? []
  const tabs = tabsFor(SCREEN, roles)
  const tab = activeTabFor(SCREEN, location.search, roles)

  return (
    <div className="tabbed">
      {/*
        The host owns the page's one h1, above the tab strip. Every body below is a TabBody and
        renders an h2, so "exactly one top-level heading per route" is structural rather than
        something each of the seven re-parented screens has to remember - and layout.spec.ts asserts
        it under strict mode, which is how the three screens that had lost theirs were found.
      */}
      <div className="page-header">
        <div>
          <PageTitle>Cases</PageTitle>
          <p>Triage, and the raw detections underneath it.</p>
        </div>
      </div>

      <div className="tab-strip" role="tablist" aria-label={SCREEN.title}>
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            // Deliberately `withTab('')` rather than `withTab(location.search)`, unlike the other
            // three hosts: changing the feed discards the rest of the query rather than carrying it.
            // Left exactly as it was - unifying the four would be a navigation change, and burying
            // one in a commit about where a list of tabs lives is how that ships unreviewed.
            onClick={() => navigate(key === SCREEN.defaultTab ? '/cases' : `/cases${withTab('', key)}`)}
            data-testid={`tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      <TabBody>
        {tab === 'raw' ? <EventStreamPage /> : tab === 'query' ? <ExplorePage /> : <AlertListPage />}
      </TabBody>
    </div>
  )
}
